import { SITE_ORIGIN } from 'helpers/conf';
import { addLocaleToPath, HREFLANG_LOCALES } from 'modules/locale/paths';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute } from 'react-navi';
import { DEFAULT_PAGE_TITLE } from '../helpers';
import { normalizeSeoPath } from '../normalize-path';
import type { ISeoMetadata } from './helpers';
import { getSeoMetadata } from './helpers';

const DEFAULT_OG_IMAGE = '/le-link-preview.jpg';

const SeoMetadata = () => {
  const {
    url: { pathname },
    title: routeTitle,
    data,
  } = useCurrentRoute();

  const {
    i18n: { language },
    t,
  } = useTranslation();

  const fallbackTitle =
    typeof routeTitle === 'function'
      ? routeTitle(t)
      : (routeTitle ?? DEFAULT_PAGE_TITLE);

  const [meta, setMeta] = useState<ISeoMetadata | undefined>();

  useEffect(() => {
    setMeta(undefined);
    getSeoMetadata(language, pathname).then(setMeta);
  }, [language, pathname]);

  const title = meta?.title ?? fallbackTitle;

  // Self-ref canonical + hreflang alternates; x-default = un-prefixed default locale.
  const seoPath = normalizeSeoPath(pathname);
  const canonicalUrl = `${SITE_ORIGIN}${addLocaleToPath(seoPath, language)}`;
  const defaultLocaleUrl = `${SITE_ORIGIN}${seoPath}`;
  const ogImage = `${SITE_ORIGIN}${data?.ogImage ?? DEFAULT_OG_IMAGE}`;

  return (
    <Helmet htmlAttributes={{ lang: language }}>
      {title && <title>{title}</title>}
      {meta?.description && (
        <meta content={meta.description} name="description" />
      )}
      <meta content={ogImage} property="og:image" />
      <link href={canonicalUrl} rel="canonical" />
      <link href={defaultLocaleUrl} hrefLang="x-default" rel="alternate" />
      {HREFLANG_LOCALES.map((locale) => (
        <link
          href={`${SITE_ORIGIN}${addLocaleToPath(seoPath, locale)}`}
          hrefLang={locale}
          key={locale}
          rel="alternate"
        />
      ))}
    </Helmet>
  );
};

export default SeoMetadata;
